


















































































































import { Vue, Component, Prop } from "vue-property-decorator";
import { Action, State } from "vuex-class";
import logger from "@/configs/logger";
import Dependants from "@/components/Dependants.vue";
import Rebuttals from "./Rebuttals.vue";
import { objectify } from "@/helpers";
import { User } from "@/types";
import { collections, firestore } from "@/configs/firebase";

@Component({ components: { Dependants, Rebuttals } })
export default class Prompt extends Vue {
  @State("user") user!: User;
  @State("taskAttributes") taskAttributes!: any;
  @Action("updateLead") updateLead!: Function;
  @Action("updateTaskAttribute") updateTaskAttribute!: Function;
  @Prop({ required: true }) leadId!: string;
  @Prop({ required: true }) pageType!: string;
  @Prop({ required: false, default: true }) showRebuttals!: boolean;

  lead: any = {};
  pages: any[] = [];
  currentPage: any = {};
  scriptValues: any = {};
  backPages: any[] = []; // A LIFO list.
  date: string = "";
  menu: string = "";
  loading: boolean = false;

  incomeLevels = [
    "0-20,000 Monthly",
    "20,001-35,000 Monthly",
    "35,001-50,000 Monthly",
    "50,001-70,000 Monthly",
    "70,001+ Monthly"
  ];

  get hasChoices(): boolean {
    const choices = this.currentPage.page_key_choices;
    return !!Object.keys(choices).length;
  }

  get showBackButton(): boolean {
    return (
      this.currentPage.prompt_name && this.currentPage.prompt_name !== "start"
    );
  }

  get usaStates(): any[] {
    const UsaStates = require("usa-states").UsaStates;
    return new UsaStates().states;
  }

  get canViewClosingRebuttal(): boolean {
    return (
      this.user.roles.includes("closer") || this.user.roles.includes("admin")
    );
  }

  get pageKeyChoices() {
    return Object.keys(this.currentPage.page_key_choices || {});
  }

  async created() {
    try {
      this.loading = true;
      await this.getPages();

      const { script_prompt_pages_history } = this.taskAttributes;
      let initialPageKey = "start";
      if (
        script_prompt_pages_history &&
        script_prompt_pages_history.selected_page_key
      ) {
        initialPageKey = script_prompt_pages_history.selected_page_key;
        this.backPages = script_prompt_pages_history.back_pages;
      }
      await this.loadPage(initialPageKey, false);
    } catch (err) {
      logger("error", "script prompt load error");
    } finally {
      this.loading = false;
    }
  }

  async getPages() {
    if (!this.pageType) {
      return logger("error", "no script prompt page type.");
    }
    const snapshot = await collections.scriptPromptPages
      .doc(this.pageType)
      .collection("pages")
      .get();

    this.pages = snapshot.docs.map(page => ({ id: page.id, ...page.data() }));
  }

  async loadPage(pageKey: string, flush = true): Promise<void> {
    try {
      const newPage = this.pages.find((item: any) => item.id == pageKey);

      if (flush) {
        await this.flushScriptValues();
      }

      if (!newPage) {
        throw new Error("Missing script prompt page.");
      }

      const snapshot = await collections.lead.doc(this.leadId).get();
      this.lead = { ...snapshot.data(), id: snapshot.id };

      // Load up the lead values
      Object.keys(newPage.inputs || {}).forEach(key => {
        const value = newPage.inputs[key];
        this.scriptValues[value.name] = this.lead[value.name] || "";
      });

      // Load up the previously selected answer (if any)
      if (this.lead && this.lead[newPage.prompt_name]) {
        this.scriptValues[newPage.prompt_name] = this.lead[newPage.prompt_name];
      }

      this.currentPage = newPage;
      logger("log", "current page", this.currentPage);
    } catch (err) {
      logger("error", "load script prompt page error", err);
      this.$toasted.error("Script prompt error").goAway(2000);
    }
  }

  async flushScriptValues(): Promise<void> {
    try {
      if (!this.lead) {
        throw new Error("No lead to flush script values");
      }

      const values: any = { last_script_update_agent_email: this.user.email };

      if (this.scriptValues.hasOwnProperty("qualified_lead_status")) {
        if (this.scriptValues["qualified_lead_status"] == "Qualified Lead") {
          const role = this.user.roles.find(role => role !== "admin");
          values[`qualifying_agent_email_by_role.${role}`] = this.user.email;
          values["qualified_ts"] = firestore.Timestamp.now();
        }
      }

      Object.keys(this.scriptValues).forEach(key => {
        let val = this.scriptValues[key];
        if (val || val !== undefined) {
          values[key] = val;
        }
      });
      await this.updateLead({
        data: values,
        leadId: this.lead.id,
        notify: false
      });
    } catch (e) {
      logger("error", e);
    }
    this.scriptValues = {};
  }

  async previousPage() {
    const prevPageKey = this.backPages.pop();
    logger("log", "checking key ", prevPageKey);
    await Promise.all([
      this.loadPage(prevPageKey),
      this.updateTaskAttribute({
        data: {
          script_prompt_pages_history: {
            selected_page_key: prevPageKey,
            selected_page_choice: "BACK",
            back_pages: this.backPages
          }
        },
        taskAttributeId: this.taskAttributes.id,
        notify: false
      })
    ]);
  }

  async nextPage(newPageChoice: string) {
    logger("log", "script", this.scriptValues);
    const prevPageKey = this.currentPage.prompt_name;
    const newPageKey = this.currentPage.page_key_choices[newPageChoice];

    // We want to store the selected choice as a script response.
    this.scriptValues[prevPageKey] = newPageChoice;
    this.backPages.push(this.currentPage.prompt_name);
    await Promise.all([
      this.loadPage(newPageKey),
      this.updateTaskAttribute({
        data: {
          script_prompt_pages_history: {
            selected_page_key: newPageKey,
            selected_page_choice: newPageChoice,
            back_pages: this.backPages
          }
        },
        taskAttributeId: this.taskAttributes.id,
        notify: false
      })
    ]);
    logger("log", "new back page ", this.currentPage.prompt_name);
  }

  alreadySelected(pageChoice: string): boolean {
    return this.scriptValues[this.currentPage.prompt_name] === pageChoice;
  }

  handleDependentChange(key: string, val: string, name: string) {
    this.scriptValues[key] = val;
  }

  handleInputChange(value: string, inputName: string, index: string) {
    const input = this.currentPage.inputs[index];
    this.scriptValues[input["name"]] = value;
  }
}
